import React, { useState } from 'react';
import {
	Box,
	Button,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	ListProps,
} from '@mui/material';

import { ProductApi } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import VisibilitySwitch from './VisibilitySwitch';
import { useTranslation } from 'services/localization';

export interface StoreVisibilitySettingsProps extends Omit<ListProps, 'onChange'> {
	value: ProductApi['visibleInLocations'];
	onChange: (value: ProductApi['visibleInLocations']) => void;
	disabled?: boolean;
}
const StoreVisibilitySettings = (props: StoreVisibilitySettingsProps) => {
	const { t } = useTranslation();
	const { value = [], onChange, disabled, ...listProps } = props;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const [showAll, setShowAll] = useState<boolean>(false);

	const isStoreEnabled = (locationId: string) => {
		return value.includes('ALL') || value.includes(locationId);
	};

	const toggleStore = (locationId: string) => (enabled: boolean) => {
		if (enabled) {
			if (value.length >= shopLocations.length - 1) {
				onChange(['ALL']);
			} else {
				onChange([...value, locationId]);
			}
		} else {
			if (value.includes('ALL')) {
				onChange(shopLocations.map((l) => l.id).filter((id) => id !== locationId));
			} else {
				onChange(value.filter((id) => id !== locationId));
			}
		}
	};

	const locationsToRender = showAll ? shopLocations : shopLocations.slice(0, 3);

	return (
		<Box>
			<List {...listProps}>
				{locationsToRender.map((location) => (
					<ListItem divider key={location.id}>
						<ListItemText primary={location.name} />
						<ListItemSecondaryAction>
							<VisibilitySwitch
								checked={isStoreEnabled(location.id)}
								onChange={toggleStore(location.id)}
								disabled={disabled}
							/>
						</ListItemSecondaryAction>
					</ListItem>
				))}
			</List>
			{shopLocations.length > 3 && (
				<Box>
					<Button onClick={() => setShowAll((prev) => !prev)}>
						{showAll
							? t('common:actions.showLess', 'Show less')
							: t('common:actions.showAll', 'Show all')}
					</Button>
				</Box>
			)}
		</Box>
	);
};

export default StoreVisibilitySettings;
